'use client';

import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, ButtonBase, Dialog } from '@mui/material';
import {
  type Dispatch,
  forwardRef,
  type MouseEvent,
  type SetStateAction,
  startTransition,
  useCallback,
  useState,
} from 'react';
import { v4 as uuid } from 'uuid';
import type { ElementViewedContextValue } from '@repo/segment/Analytics/ElementViewed';
import { Link } from './components/Link';
import { SectionName } from './components/SectionName';
import { Title } from './components/Title';
import { scrollToViewPromise } from './scrollToViewPromise';
import type { TableOfContentsHeadingProps } from './types';
import { useHeadingsObserver } from './useHeadingObserver';
import { COMPONENT_ID_MOBILE } from './utils';

type Props = {
  distanceToSectionWrapper: number;
  headingLevel: number;
  headings: TableOfContentsHeadingProps[];
  tocId: string;
  activeId: string;
  setActiveId: Dispatch<SetStateAction<string>>;
  fireNavigateToSectionPageAction: (
    overrideProps?: Record<string, string | object>,
    callback?: () => void,
  ) => void;
  /** Must be provided from an ElementViewed render-function context. */
  fireElementInteracted: ElementViewedContextValue['fireElementInteracted'];
};

export const Mobile = forwardRef<HTMLElement, Props>(function Mobile(
  {
    distanceToSectionWrapper,
    headingLevel,
    headings,
    tocId,
    activeId,
    setActiveId,
    fireNavigateToSectionPageAction,
    fireElementInteracted,
  }: Props,
  ref,
) {
  const [isDialogVisible, setDialogVisible] = useState(false);

  const { pauseScrollTracking, reenableScrollTracking } = useHeadingsObserver({
    activeId,
    distanceToSectionWrapper,
    headingLevel,
    headings,
    setActiveId,
    tocId: COMPONENT_ID_MOBILE,
  });

  const viewDetailsOnClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    // Small delay to prevent click-through on mobile
    setTimeout(() => {
      startTransition(() => {
        setDialogVisible(true);
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement) {
          activeElement.blur();
        }
      });
    }, 0);
  };

  const close = () => {
    setDialogVisible(false);
  };

  const handleClick = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      event.stopPropagation();
      close();
      const href = event.currentTarget.href;
      const headingId = href.substring(href.indexOf('#') + 1);
      const headingContent = event.currentTarget.innerText;
      pauseScrollTracking();

      startTransition(() => {
        setActiveId(headingId);
      });

      fireNavigateToSectionPageAction({
        action_input: `${headings.findIndex(element => element.id === headingId)}`,
        action_location_url: window.location.origin,
        activity_id: uuid(),
        additional_attributes: {
          toc_section: headingContent,
        },
      });
      fireElementInteracted('navigate', {
        entityName: 'toc_interacted',
        itemPosition: headings.findIndex(element => element.id === headingId),
        additionalAttributes: { toc_section: headingContent },
      });

      await scrollToViewPromise(headingId, 'smooth', 120, true);

      reenableScrollTracking();
    },
    [JSON.stringify(headings), fireNavigateToSectionPageAction, fireElementInteracted],
  );

  const activeHeader = headings.find(heading => heading.id === activeId)?.header;
  return (
    <Box
      ref={ref}
      id={tocId}
      sx={{
        display: { xs: 'block', md: 'none' },
        backgroundColor: 'white',
        width: '100vw',
        position: 'relative',
        left: { xs: 'calc(-1 * (100vw - 100%) / 2)' },
        borderBottom: '1px solid #D8D9DA',
        boxShadow: '0px 8px 8px 0px #64666A26',
      }}
    >
      <ButtonBase
        onClick={viewDetailsOnClick}
        disableRipple
        sx={{
          display: 'block',
          width: '100%',
          paddingTop: 3,
          '&:hover': { backgroundColor: 'neutral.lightest' },
          '&:active': {
            outline: '2px solid',
            outlineOffset: '-2px',
            outlineColor: 'secondary.main',
            outlineStyle: 'auto',
          },
        }}
      >
        <Box
          component="span"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            paddingX: 3,
            marginBottom: 2,
          }}
        >
          <Title />
          <ExpandMoreIcon sx={{ fontSize: '24px' }} />
        </Box>
        {activeHeader && (
          <Box
            component="span"
            sx={{
              paddingY: 2,
              paddingX: 3,
              backgroundColor: 'blues.lightest',
              borderLeft: '4px solid',
              borderLeftColor: 'primary.main',
              display: 'flex',
              textAlign: 'left',
            }}
          >
            <SectionName bold>{activeHeader}</SectionName>
          </Box>
        )}
      </ButtonBase>
      <Dialog
        open={isDialogVisible}
        onClose={close}
        fullScreen
        slotProps={{
          paper: {
            onClick: (e: React.MouseEvent) => e.stopPropagation(),
            sx: {
              alignSelf: 'flex-end',
              borderWidth: '1px',
              borderStyle: 'solid',
              marginTop: { xs: '20vh', sm: '45vh' },
              minWidth: '100%',
              borderRadius: '10px 10px 0px 0px',
              padding: 3,
              height: { xs: '80vh', sm: '55vh' },
            },
          },
        }}
      >
        <Box component="nav" aria-label="table of contents">
          <Box
            sx={{
              padding: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '4px solid',
              borderColor: 'primary.main',
            }}
          >
            <Title isHeading />
            <ButtonBase disableRipple onClick={close}>
              <CloseIcon sx={{ fontSize: '24px' }} />
            </ButtonBase>
          </Box>
          <Box
            sx={{ padding: 2, mt: 0, overflow: 'auto' }}
            component="ol"
            aria-labelledby="on-this-page"
          >
            {headings.map(heading => (
              <Box component="li" key={heading.id} sx={{ listStyle: 'none' }}>
                <Link
                  key={heading.id}
                  isSelected={activeId === heading.id}
                  headingId={heading.id}
                  onClick={handleClick}
                  {...(heading.id === activeId && {
                    className: 'selected',
                  })}
                >
                  {heading.header}
                </Link>
              </Box>
            ))}
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
});
